import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles, X } from "lucide-react";
import { Button } from "./ui/button";

const SPOTLIGHT_PADDING = 8;
const TOOLTIP_GAP = 16;
const VIEWPORT_MARGIN = 16;
const TOOLTIP_MAX_WIDTH = 340;

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

const getTargetRect = (selector) => {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    top: r.top - SPOTLIGHT_PADDING,
    left: r.left - SPOTLIGHT_PADDING,
    width: r.width + SPOTLIGHT_PADDING * 2,
    height: r.height + SPOTLIGHT_PADDING * 2,
    bottom: r.bottom + SPOTLIGHT_PADDING,
    right: r.right + SPOTLIGHT_PADDING,
  };
};

export const OnboardingTour = ({ steps, open, onClose, storageKey }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: TOOLTIP_MAX_WIDTH });
  const tooltipRef = useRef(null);
  const wasOpenRef = useRef(open);

  // Reset to the first step synchronously whenever the tour transitions
  // from closed to open, so no stale step ever flashes on screen.
  if (open && !wasOpenRef.current && stepIndex !== 0) {
    setStepIndex(0);
  }
  wasOpenRef.current = open;

  const step = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const finish = useCallback(() => {
    try {
      localStorage.setItem(storageKey, "1");
    } catch (e) {
      // ignore storage errors (private mode, etc.)
    }
    onClose();
  }, [onClose, storageKey]);

  const updateRect = useCallback(() => {
    if (!step) return;
    if (!step.target) {
      setRect(null);
      return;
    }
    const r = getTargetRect(step.target);
    setRect(r);
  }, [step]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    // Scroll instantly (not smoothly) so the target's rect is correct the
    // moment we read it — a smooth scroll leaves the spotlight and tooltip
    // racing the animation and briefly out of sync with the real layout.
    const el = step?.target ? document.querySelector(step.target) : null;
    if (el) {
      const r = el.getBoundingClientRect();
      const margin = 24;
      const inView = r.top >= margin && r.bottom <= window.innerHeight - margin;
      if (!inView) {
        el.scrollIntoView({ behavior: "auto", block: "center" });
      }
    }
    updateRect();
    const handle = () => updateRect();
    window.addEventListener("resize", handle);
    window.addEventListener("scroll", handle, true);
    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("scroll", handle, true);
    };
  }, [open, step, updateRect]);

  const repositionTooltip = useCallback(() => {
    const el = tooltipRef.current;
    if (!el) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Width is driven from JS (not a Tailwind arbitrary-value class) so
    // positioning never depends on a CSS rule having compiled correctly.
    // Apply it to the DOM directly first so the height we measure right
    // after reflects real text wrapping at that width.
    const tw = Math.min(TOOLTIP_MAX_WIDTH, vw - VIEWPORT_MARGIN * 2);
    el.style.width = `${tw}px`;
    const th = el.offsetHeight;
    if (!th) return; // not painted yet

    let top;
    let left;
    if (!rect) {
      top = vh / 2 - th / 2;
      left = vw / 2 - tw / 2;
    } else {
      const placement = step?.placement || "bottom";
      switch (placement) {
        case "top":
          top = rect.top - TOOLTIP_GAP - th;
          left = rect.left + rect.width / 2 - tw / 2;
          break;
        case "left":
          top = rect.top + rect.height / 2 - th / 2;
          left = rect.left - TOOLTIP_GAP - tw;
          break;
        case "right":
          top = rect.top + rect.height / 2 - th / 2;
          left = rect.right + TOOLTIP_GAP;
          break;
        default:
          top = rect.bottom + TOOLTIP_GAP;
          left = rect.left + rect.width / 2 - tw / 2;
      }
    }

    left = clamp(left, VIEWPORT_MARGIN, vw - tw - VIEWPORT_MARGIN);
    top = clamp(top, VIEWPORT_MARGIN, vh - th - VIEWPORT_MARGIN);
    setPos({ top, left, width: tw });
  }, [rect, step]);

  useLayoutEffect(() => {
    if (!open) return;
    repositionTooltip();
  }, [open, repositionTooltip]);

  // Webfonts or images loading late inside the tooltip can change its size
  // after the first measurement — keep it correctly positioned when that
  // happens instead of leaving it wherever the first (wrong) size put it.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const el = tooltipRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => repositionTooltip());
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, stepIndex, repositionTooltip]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") finish();
      if (e.key === "ArrowRight") setStepIndex((i) => Math.min(i + 1, steps.length - 1));
      if (e.key === "ArrowLeft") setStepIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, finish, steps.length]);

  if (!open || !step) return null;

  const next = () => {
    if (isLast) finish();
    else setStepIndex((i) => i + 1);
  };
  const back = () => setStepIndex((i) => Math.max(0, i - 1));

  return createPortal(
    <div className="fixed inset-0 z-[100]" data-testid="tutorial-tour">
      {/* Click-catcher backdrop: dismisses the tour on any outside click */}
      <div
        className="fixed inset-0 bg-transparent"
        onClick={finish}
        data-testid="tutorial-backdrop"
      />

      {/* Dark overlay with spotlight cutout over the current target */}
      {rect ? (
        <motion.div
          className="fixed rounded-2xl pointer-events-none"
          style={{ boxShadow: "0 0 0 9999px rgba(28,25,23,0.68)" }}
          animate={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
          transition={{ type: "spring", stiffness: 320, damping: 32 }}
        />
      ) : (
        <div className="fixed inset-0 bg-stone-900/68 backdrop-blur-[1px] pointer-events-none" />
      )}

      <motion.div
        key={stepIndex}
        ref={tooltipRef}
        initial={{ opacity: 0, y: 6, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="fixed z-[101] rounded-2xl bg-[#FDFBF7] border border-stone-900/10 shadow-[0_20px_60px_rgba(28,25,23,0.28)] p-5"
        style={{ top: pos.top, left: pos.left, width: pos.width }}
        onClick={(e) => e.stopPropagation()}
        data-testid="tutorial-tooltip"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-1.5 text-[#B8933F]">
            <Sparkles className="w-3.5 h-3.5" />
            <span className="font-[Outfit] text-[10px] tracking-[0.2em] uppercase text-stone-500">
              Passo {stepIndex + 1} de {steps.length}
            </span>
          </div>
          <button
            type="button"
            onClick={finish}
            aria-label="Fechar tutorial"
            data-testid="tutorial-close"
            className="-mt-1 -mr-1 p-1 rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-900/5 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <h3 className="mt-3 font-[Outfit] text-lg text-stone-900">{step.title}</h3>
        <p className="mt-1.5 font-[Outfit] text-sm text-stone-600 leading-relaxed">
          {step.content}
        </p>

        <div className="mt-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5" aria-hidden="true">
            {steps.map((_, i) => (
              <span
                key={i}
                className={[
                  "h-1.5 rounded-full transition-all",
                  i === stepIndex ? "w-4 bg-stone-900" : "w-1.5 bg-stone-900/20",
                ].join(" ")}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {!isFirst && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={back}
                data-testid="tutorial-back"
                className="rounded-full h-8 px-3 text-xs font-[Outfit] text-stone-600 hover:bg-stone-900/5"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Voltar
              </Button>
            )}
            <Button
              type="button"
              onClick={next}
              size="sm"
              data-testid={isLast ? "tutorial-finish" : "tutorial-next"}
              className="rounded-full h-8 px-4 text-xs font-[Outfit] bg-stone-900 hover:bg-stone-800 text-stone-50"
            >
              {isLast ? "Concluir" : "Próximo"}
              {!isLast && <ArrowRight className="w-3.5 h-3.5 ml-1" />}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>,
    document.body,
  );
};

export default OnboardingTour;
